import { useEffect, useMemo, useRef, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { redirectToLogin } from "../utils/auth";
import { useUser } from "../context/UserContext";

// AsyncPaginate styled to match the app (jiojbm purple focus).
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "8px",
    borderColor: state.isFocused ? "#6D17CE" : "#d1d5db",
    boxShadow: "none",
    minHeight: "38px",
    fontSize: "13px",
    width: "260px",
  }),
};

// "Pending Assistants Status" dashboard. Org list + assistants come from the V2
// endpoints (/getAllOrg, /getAllBots) — Bearer-token scoped. Pagination is
// SERVER-SIDE: one page (offset/limit) is fetched per request. LOA
// status/comment are stored via V3 (PUT /API/V3/assistant/:id) on edit only.
const BASE = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZES = [10, 25, 50]; // JBM caps a page at 50
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

// LOA statuses come from config (/API/V2/config/loa-status), like ticket statuses:
// [{ key, value, isDefault, isFinal }]. Only colours live here, assigned by the
// status's position in that list (sorted by key), so new statuses still get one.
const STATUS_COLORS = [
  { pill: "bg-amber-100 text-amber-700", card: "bg-amber-500" },
  { pill: "bg-red-100 text-red-700", card: "bg-red-500" },
  { pill: "bg-emerald-100 text-emerald-700", card: "bg-emerald-600" },
  { pill: "bg-sky-100 text-sky-700", card: "bg-sky-600" },
  { pill: "bg-gray-100 text-gray-700", card: "bg-gray-500" },
];

export const AssistantStatus = () => {
  const { user } = useUser();
  const isInternal = user.loaded ? user.isPartner === false : null;
  const isPartner = user.loaded ? user.isPartner === true : false;
  // Status/comment edits (single and batch) are for internal team-0 users only —
  // matches server-side requireTeamZero. Partners are read-only.
  const canEdit = isInternal === true && String(user.team) === "0";
  // LOA file upload/removal: partners and internal team-0 users (matches
  // server-side partnerOrTeamZero). Everyone else can only view/download.
  const canManageFiles = isPartner || canEdit;

  const [account, setAccount] = useState(null); // selected org option { label, value }
  const [assistants, setAssistants] = useState([]); // current page only
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [loaStatuses, setLoaStatuses] = useState([]); // config: [{ key, value, isDefault, isFinal }]
  // Status key, or "all" for no status filter (the "Total" card).
  // Driven by the clickable cards; null until the status config has loaded.
  const [statusFilter, setStatusFilter] = useState(null);
  const [subAccountFilter, setSubAccountFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Batch update (internal team-0 only)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkComment, setBulkComment] = useState("");
  const [applying, setApplying] = useState(false);
  const clearSelection = () => { setSelectedIds(new Set()); setBulkStatus(""); setBulkComment(""); };

  // Full-comment hover popup. Rendered with position:fixed so the table's
  // scrolling container can't clip it. { text, left, top, above } or null.
  const [commentTip, setCommentTip] = useState(null);
  const showCommentTip = (e, text) => {
    // No popup for an empty comment, or while the user is typing in this box.
    if (!text || e.currentTarget.contains(document.activeElement)) return;
    const r = e.currentTarget.getBoundingClientRect();
    const above = r.bottom + 160 > window.innerHeight; // flip up near the bottom of the screen
    const left = Math.min(r.left, window.innerWidth - 336); // keep the 320px popup on screen
    setCommentTip({ text, left: Math.max(8, left), top: above ? r.top - 4 : r.bottom + 4, above });
  };
  const hideCommentTip = () => setCommentTip(null);
  // A fixed-position popup would drift from its row on scroll — just close it.
  useEffect(() => {
    if (!commentTip) return;
    window.addEventListener("scroll", hideCommentTip, true); // capture: table container scrolls too
    return () => window.removeEventListener("scroll", hideCommentTip, true);
  }, [commentTip]);

  // LOA files modal
  const [mediaFor, setMediaFor] = useState(null); // assistant row the modal is open for, or null
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Status config helpers. defaultKey is what an assistant has before it's edited.
  const defaultKey = loaStatuses.find((s) => s.isDefault)?.key ?? loaStatuses[0]?.key ?? null;
  const isFinalStatus = (key) => loaStatuses.some((s) => s.key === key && s.isFinal);
  const statusLabel = (key) => loaStatuses.find((s) => s.key === key)?.value ?? "—";
  const statusColor = (key) => {
    const i = loaStatuses.findIndex((s) => s.key === key);
    return STATUS_COLORS[i >= 0 ? i % STATUS_COLORS.length : STATUS_COLORS.length - 1];
  };

  // Load the LOA status config once. The list waits for it so rows get the right default.
  const loadStatuses = async () => {
    const res = await fetch(`${BASE}/API/V2/config/loa-status`, { headers: authHeaders() });
    if (res.status === 401) { redirectToLogin(); return []; }
    const data = res.ok ? await res.json() : [];
    const sorted = (Array.isArray(data) ? data : []).sort((a, b) => a.key - b.key);
    setLoaStatuses(sorted);
    setStatusFilter((cur) => cur ?? (sorted.find((s) => s.isDefault)?.key ?? sorted[0]?.key ?? null));
    return sorted;
  };

  // Paginated + searchable org loader for the AsyncPaginate dropdown.
  const loadOrgOptions = async (searchQuery, _loaded, { page }) => {
    const limit = 50;
    const offset = (page - 1) * limit;
    const res = await fetch(
      `${BASE}/API/V2/getAllOrg?offset=${offset}&limit=${limit}&search=${encodeURIComponent(searchQuery)}`,
      { headers: authHeaders() },
    );
    if (res.status === 401) { redirectToLogin(); return { options: [], hasMore: false }; }
    if (!res.ok) return { options: [], hasMore: false };
    const d = await res.json();
    const names = Array.isArray(d) ? d : d.organizations || [];
    const options = names.map((name) => ({ label: name, value: name }));
    const total = Number.isFinite(d.totalCount) ? d.totalCount : offset + names.length;
    return { options, hasMore: offset + limit < total, additional: { page: page + 1 } };
  };

  // Fetch one page of assistants (server-side) + merge stored LOA states.
  // No account selected → no org filter (internal sees all; external is
  // token-scoped). Selecting an account adds the organization filter.
  const fetchPage = async (pageNum, statusConfig = loaStatuses) => {
    const fallbackStatus = statusConfig.find((s) => s.isDefault)?.key ?? statusConfig[0]?.key ?? null;
    setLoading(true);
    clearSelection(); // account / page / search change → new set of rows
    try {
      const offset = (pageNum - 1) * pageSize;
      const org = isInternal ? account?.value || "" : "";
      const url =
        `${BASE}/API/V2/getAllBots?offset=${offset}&limit=${pageSize}` +
        `&search=${encodeURIComponent(search.trim())}&approvalStatus=pending&organization=${encodeURIComponent(org || "")}`;

      const botsRes = await fetch(url, { headers: authHeaders() }).then((r) => {
        if (r.status === 401) { redirectToLogin(); return null; }
        return r.ok ? r.json() : { bots: [], totalCount: 0 };
      });
      if (!botsRes) return;

      const bots = botsRes.bots || [];

      const base = bots.map((b) => ({
        assistantId: b.botId,
        organizationId: b.organizationId || "",
        name: b.displayName || "",
        subAccount: b.subAccount || "",
        usecase: b.usecase || "",
        status: fallbackStatus,
        comment: "",
        createdAt: b.createdAt || null, // assistant created (JBM)
        updatedAt: b.updatedAt || null, // assistant last modified (JBM)
      }));

      // Fetch stored LOA state (status/comment) per assistant for this page. A
      // null response means it was never edited → keep the pending/empty default.
      const states = await Promise.all(
        base.map((row) =>
          fetch(`${BASE}/API/V3/assistant/${encodeURIComponent(row.assistantId)}`, { headers: authHeaders() })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ),
      );

      setAssistants(base.map((row, i) => {
        const s = states[i];
        return s
          ? {
              ...row,
              status: s.status != null ? Number(s.status) : fallbackStatus,
              comment: s.comment || "",
              approvedOn: s.approvedOn || null,
              updatedAt: s.updatedAt || row.updatedAt, // our LOA edit time when we have one
            }
          : row;
      }));
      setTotalCount(Number.isFinite(botsRes.totalCount) ? botsRes.totalCount : bots.length);
      setPage(pageNum);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Initial load once the user is known: status config first (rows need its
  // default), then page 1. Internal loads ALL assistants (no org filter);
  // external loads their token-scoped assistants.
  useEffect(() => {
    if (!user.loaded) return;
    if (!localStorage.getItem("authToken")) { redirectToLogin(); return; }
    loadStatuses().then((config) => fetchPage(1, config));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.loaded]);

  // Reload when the selected org or page size changes; skip first run.
  const firstFilter = useRef(true);
  useEffect(() => {
    if (firstFilter.current) { firstFilter.current = false; return; }
    if (!user.loaded) return;
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, pageSize]);

  // Debounced reload (page 1) on search text change; skip first render.
  const firstSearch = useRef(true);
  useEffect(() => {
    if (firstSearch.current) { firstSearch.current = false; return; }
    if (!user.loaded) return;
    const t = setTimeout(() => fetchPage(1), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const patchLocal = (assistantId, patch) =>
    setAssistants((prev) => prev.map((a) => (a.assistantId === assistantId ? { ...a, ...patch } : a)));

  const updateAssistant = async (assistantId, body) => {
    patchLocal(assistantId, body);
    const organizationId = assistants.find((a) => a.assistantId === assistantId)?.organizationId || "";
    await fetch(`${BASE}/API/V3/assistant/${encodeURIComponent(assistantId)}`, {
      method: "PUT", headers: authHeaders(), body: JSON.stringify({ organizationId, ...body }),
    });
  };

  // ── LOA files ──────────────────────────────────────────────────────────────
  const mediaBase = (assistantId) => `${BASE}/API/V3/assistant/${encodeURIComponent(assistantId)}/media`;

  const fetchMedia = async (assistantId) => {
    setMediaLoading(true);
    try {
      const res = await fetch(mediaBase(assistantId), { headers: authHeaders() });
      setMediaFiles(res.ok ? await res.json() : []);
    } finally {
      setMediaLoading(false);
    }
  };

  const openMedia = (row) => { setMediaFor(row); setMediaFiles([]); fetchMedia(row.assistantId); };
  const closeMedia = () => { setMediaFor(null); setMediaFiles([]); };

  const uploadMedia = async (fileList) => {
    if (!mediaFor || !fileList?.length) return;
    const form = new FormData();
    [...fileList].forEach((f) => form.append("files", f));
    // Sets the record's organization if this is the assistant's first save.
    form.append("organizationId", mediaFor.organizationId || "");
    setUploading(true);
    try {
      // Note: no Content-Type header — the browser sets the multipart boundary.
      const res = await fetch(mediaBase(mediaFor.assistantId), {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: form,
      });
      if (!res.ok) { alert("Upload failed"); return; }
      await fetchMedia(mediaFor.assistantId);
    } finally {
      setUploading(false);
    }
  };

  const downloadMedia = async (name) => {
    const res = await fetch(`${mediaBase(mediaFor.assistantId)}/${encodeURIComponent(name)}`, { headers: authHeaders() });
    if (!res.ok) { alert("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const deleteMedia = async (name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    const res = await fetch(`${mediaBase(mediaFor.assistantId)}/${encodeURIComponent(name)}`, {
      method: "DELETE", headers: authHeaders(),
    });
    if (!res.ok) { alert("Delete failed"); return; }
    await fetchMedia(mediaFor.assistantId);
  };

  const subAccounts = useMemo(
    () => Array.from(new Set(assistants.map((a) => a.subAccount).filter(Boolean))).sort(),
    [assistants],
  );

  // This page's assistants in the selected sub-account. The tile counts come from
  // this list, so they follow the sub-account filter but not the status filter.
  const inSubAccount = useMemo(
    () => (subAccountFilter === "all" ? assistants : assistants.filter((a) => a.subAccount === subAccountFilter)),
    [assistants, subAccountFilter],
  );

  const rows = useMemo(() => {
    // No sorting — keep the order as returned by the API.
    return inSubAccount.filter((a) => statusFilter === "all" || a.status === statusFilter); // selected via the cards
  }, [inSubAccount, statusFilter]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(loaStatuses.map((s) => [s.key, 0]));
    inSubAccount.forEach((a) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [inSubAccount, loaStatuses]);

  // ── Batch selection ────────────────────────────────────────────────────────
  // Approved rows can't be selected. The header checkbox covers only the
  // selectable rows currently shown (after search + filters).
  const isSelectable = (a) => !isFinalStatus(a.status);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectableRows = useMemo(() => rows.filter(isSelectable), [rows, loaStatuses]);
  const allVisibleSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.assistantId));
  const someVisibleSelected = selectableRows.some((r) => selectedIds.has(r.assistantId));

  const headerCheckbox = useRef(null);
  useEffect(() => {
    if (headerCheckbox.current) headerCheckbox.current.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllVisible = (checked) => setSelectedIds((prev) => {
    const next = new Set(prev);
    selectableRows.forEach((r) => (checked ? next.add(r.assistantId) : next.delete(r.assistantId)));
    return next;
  });

  // Switching view (status card or sub-account filter) clears the selection.
  useEffect(() => { clearSelection(); }, [statusFilter, subAccountFilter]);

  const canApply = bulkStatus !== "" || bulkComment.trim() !== "";

  const applyBulk = async () => {
    if (!canApply || applying) return;
    const targets = assistants.filter((a) => selectedIds.has(a.assistantId) && isSelectable(a));
    if (!targets.length) { clearSelection(); return; }

    if (bulkStatus !== "" && isFinalStatus(Number(bulkStatus))) {
      // Final status (Approved) → "Approve 6 assistants? This cannot be undone."
      const n = targets.length;
      const label = statusLabel(Number(bulkStatus));
      const action = label === "Approved" ? "Approve" : `Mark as "${label}"`;
      if (!window.confirm(`${action} ${n} assistant${n === 1 ? "" : "s"}? This cannot be undone.`)) return;
    }

    const body = { items: targets.map((a) => ({ assistantId: a.assistantId, organizationId: a.organizationId })) };
    if (bulkStatus) body.status = Number(bulkStatus);
    if (bulkComment.trim()) body.comment = bulkComment.trim();

    setApplying(true);
    try {
      const res = await fetch(`${BASE}/API/V3/assistant/bulk`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify(body),
      });
      if (res.status === 401) { redirectToLogin(); return; }
      if (!res.ok) { alert("Batch update failed. No changes were applied."); return; }
      const result = await res.json();
      const modified = new Set(result.modified || []);

      setAssistants((prev) => prev.map((a) => {
        if (!modified.has(a.assistantId)) return a;
        return {
          ...a,
          ...(result.status ? { status: Number(result.status), approvedOn: result.approvedOn } : {}),
          ...(result.comment ? { comment: result.comment } : {}),
          updatedAt: result.updatedAt,
        };
      }));
      if (result.skipped?.length) {
        alert(`${result.skipped.length} assistant(s) were already approved and were left unchanged.`);
      }
      clearSelection();
    } finally {
      setApplying(false);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  const fieldSelect = "border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-jiojbmpurple";
  const fieldLabel = "text-[11px] font-semibold uppercase tracking-wide text-gray-500";
  const th = "py-2 px-3 border-b text-left text-[11px] font-bold uppercase tracking-wide text-gray-500 whitespace-nowrap";
  const td = "py-1.5 px-3 border-b align-middle";
  const dash = <span className="text-gray-400">—</span>;

  return (
    <div className="flex flex-col gap-3 p-4 pt-5 mx-3 text-jiojbm80">
      <div>
        <p className="text-[11px] font-bold tracking-wide uppercase text-jiojbmpurple">LOA Management</p>
        <h1 className="text-2xl font-black tracking-tight text-jiojbm50">
          Pending Assistants Status
          {loaded && (
            <span className="ml-3 text-base font-bold text-jiojbmpurple">Total Assistants: {totalCount}</span>
          )}
        </h1>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3">
        {isInternal && (
          <div className="flex flex-col gap-1">
            <label className={fieldLabel}>Account</label>
            <AsyncPaginate
              value={account}
              loadOptions={loadOrgOptions}
              onChange={setAccount}
              additional={{ page: 1 }}
              placeholder="Select account…"
              isSearchable
              isClearable
              debounceTimeout={300}
              styles={selectStyles}
              loadingMessage={() => "Loading accounts…"}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className={fieldLabel}>Filter by sub-account</label>
          <select className={fieldSelect} value={subAccountFilter} onChange={(e) => setSubAccountFilter(e.target.value)}>
            <option value="all">All sub-accounts</option>
            {subAccounts.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
          <label className={fieldLabel}>Search assistant by name or ID</label>
          {/* overflow-hidden keeps the input inside the pill's rounded right edge */}
          <div className="flex items-center bg-white rounded-full border border-gray-300 py-0.5 overflow-hidden focus-within:border-jiojbmpurple">
            <span className="w-5 h-5 shrink-0 fill-gray-400 ml-3 mr-1">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
              </svg>
            </span>
            <input className="w-full min-w-0 bg-transparent pl-1 pr-4 py-1.5 text-sm text-gray-700 focus:outline-none" type="search"
              value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Enter assistant name or ID" />
          </div>
        </div>
      </div>

      {/* Summary cards — page total first (clears the status filter), then one
          filter card per configured status. All clickable. The overall total is
          shown next to the page title. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          // Clicking this clears the status filter: shows every status on the page.
          { n: loaded ? inSubAccount.length : "—", l: "Total", c: "bg-jiojbm50", s: "all" },
          ...loaStatuses.map((s) => ({
            n: loaded ? counts[s.key] ?? 0 : "—",
            l: s.value,
            c: statusColor(s.key).card,
            s: s.key,
          })),
        ].map((card) => {
          const isStatusCard = card.s !== undefined;
          const active = isStatusCard && statusFilter === card.s;
          return (
            <div
              key={card.l}
              onClick={isStatusCard ? () => setStatusFilter(card.s) : undefined}
              className={`rounded-2xl p-4 text-white ${card.c} transition ${isStatusCard ? "cursor-pointer hover:opacity-95" : ""} ${active ? "ring-4 ring-jiojbm50 ring-offset-2 scale-[1.02]" : isStatusCard ? "opacity-80" : ""}`}
            >
              <div className="text-2xl font-black">{card.n}</div>
              <div className="text-xs font-semibold opacity-90 mt-1">{card.l}</div>
            </div>
          );
        })}
      </div>

      {/* Batch action bar — internal team-0 only, shown once something is selected */}
      {canEdit && selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-jiojbm50 text-white rounded-xl px-4 py-2.5">
          <span className="text-sm font-semibold whitespace-nowrap">{selectedIds.size} selected</span>
          <select className="rounded-lg px-3 py-1.5 text-sm text-gray-700" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
            <option value="">Status (unchanged)</option>
            {loaStatuses.map((s) => <option key={s.key} value={s.key}>{s.value}</option>)}
          </select>
          <input className="rounded-lg px-3 py-1.5 text-sm text-gray-700 flex-1 min-w-[200px]" type="text"
            value={bulkComment} onChange={(e) => setBulkComment(e.target.value)} placeholder="Comment (leave blank to keep existing)" />
          <button
            className="bg-white text-jiojbm50 font-bold rounded-full px-4 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canApply || applying}
            title={!canApply ? "Set a status or a comment to apply" : undefined}
            onClick={applyBulk}
          >
            {applying ? "Applying…" : "Apply"}
          </button>
          <button className="border border-white/40 rounded-full px-4 py-1.5 text-sm hover:bg-white/10" onClick={clearSelection} disabled={applying}>
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-auto border border-gray-300 rounded-lg">
        <table className="min-w-full bg-white" style={{ fontSize: "12px" }}>
          <thead>
            <tr className="bg-gray-50">
              {canEdit && (
                <th className={`${th} w-9 text-center`}>
                  <input
                    ref={headerCheckbox}
                    type="checkbox"
                    aria-label="Select all shown pending and on-hold assistants"
                    checked={allVisibleSelected}
                    disabled={selectableRows.length === 0}
                    onChange={(e) => toggleAllVisible(e.target.checked)}
                  />
                </th>
              )}
              <th className={th}>Assistant Name</th>
              <th className={th}>Assistant ID</th>
              <th className={th}>Sub-Account</th>
              <th className={th}>Usecase</th>
              <th className={th}>Status</th>
              <th className={th}>Comment</th>
              <th className={th}>File Upload</th>
              <th className={th}>Created</th>
              <th className={th}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.assistantId} className={selectedIds.has(a.assistantId) ? "bg-jiojbm20" : "hover:bg-gray-50"}>
                {canEdit && (
                  <td className={`${td} text-center`}>
                    {isSelectable(a) && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${a.name || a.assistantId}`}
                        checked={selectedIds.has(a.assistantId)}
                        onChange={() => toggleRow(a.assistantId)}
                      />
                    )}
                  </td>
                )}
                <td className={`${td} font-semibold`}>{a.name}</td>
                <td className={`${td} font-mono text-gray-500`}>{a.assistantId}</td>
                <td className={td}>{a.subAccount || dash}</td>
                <td className={td}>{a.usecase || dash}</td>
                <td className={td}>
                  <select
                    className={`rounded-full text-[11px] font-bold px-2.5 py-1 border-none focus:outline-none cursor-pointer disabled:cursor-default ${statusColor(a.status).pill}`}
                    value={a.status} disabled={!canEdit}
                    onChange={(e) => updateAssistant(a.assistantId, { status: Number(e.target.value) })}
                  >
                    {loaStatuses.map((s) => <option key={s.key} value={s.key}>{s.value}</option>)}
                  </select>
                </td>
                <td className={td}>
                  {/* Hovering shows the full saved comment in a popup (see commentTip).
                      Handlers sit on the wrapper because disabled inputs (read-only
                      users) don't reliably fire mouse events. */}
                  <div onMouseEnter={(e) => showCommentTip(e, a.comment)} onMouseLeave={hideCommentTip}>
                    <input
                      key={a.comment} // remount after a batch comment so the new text shows
                      className="w-full min-w-[160px] bg-white px-2 py-1 rounded border border-gray-300 text-xs text-black truncate hover:border-gray-400 focus:border-jiojbmpurple focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 disabled:pointer-events-none"
                      type="text" defaultValue={a.comment} disabled={!canEdit} placeholder="Add a comment…"
                      aria-label={a.comment ? `Comment: ${a.comment}` : "Comment"}
                      onFocus={hideCommentTip} // no popup while typing
                      onBlur={(e) => {
                        // Trim so added/removed spaces aren't treated as a change.
                        const next = e.target.value.trim();
                        if (next !== (a.comment || "").trim()) updateAssistant(a.assistantId, { comment: next });
                        else e.target.value = a.comment || ""; // undo whitespace-only edits
                      }}
                    />
                  </div>
                </td>
                <td className={td}>
                  <button
                    className="inline-flex items-center gap-1 text-xs font-semibold text-jiojbmpurple hover:underline"
                    onClick={() => openMedia(a)}
                  >
                    📎 {canManageFiles ? "Upload / View" : "View"}
                  </button>
                </td>
                <td className={`${td} text-gray-500`}>{fmtDate(a.createdAt)}</td>
                <td className={`${td} text-gray-500`}>{fmtDate(a.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && <p className="text-xs text-gray-500">Loading assistants…</p>}
      {!loading && loaded && rows.length === 0 && <p className="text-xs text-gray-500">No assistants match this filter on this page.</p>}
      {!loading && !loaded && <p className="text-xs text-gray-500">No assistants found.</p>}

      {/* Pagination */}
      {loaded && totalCount > 0 && (
        <div className="flex flex-wrap items-center gap-4 px-1">
          <button className="px-3 py-1 rounded-full border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            disabled={page <= 1 || loading} onClick={() => fetchPage(page - 1)}>‹ Prev</button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages} · {totalCount} total</span>
          <button className="px-3 py-1 rounded-full border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            disabled={page >= totalPages || loading} onClick={() => fetchPage(page + 1)}>Next ›</button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm font-bold text-jiojbmpurple whitespace-nowrap">Per page</span>
            <select className="border border-gray-300 rounded-full px-3 py-1 text-sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Full-comment hover popup */}
      {commentTip && (
        <div
          role="tooltip"
          className="pointer-events-none fixed z-50 w-max max-w-[320px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs leading-relaxed text-black shadow-lg whitespace-pre-wrap break-words"
          style={{
            left: commentTip.left,
            top: commentTip.top,
            transform: commentTip.above ? "translateY(-100%)" : undefined,
          }}
        >
          {commentTip.text}
        </div>
      )}

      {/* LOA files modal */}
      {mediaFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeMedia}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-jiojbmpurple">LOA Files</p>
                <h3 className="text-lg font-black text-jiojbm50 leading-tight">{mediaFor.name || mediaFor.assistantId}</h3>
              </div>
              <button className="text-gray-400 hover:text-gray-700 text-xl leading-none" onClick={closeMedia}>×</button>
            </div>

            {/* Upload (partners + internal team-0) */}
            {canManageFiles && (
              <label className={`mt-4 flex items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-5 cursor-pointer text-sm ${uploading ? "opacity-60 pointer-events-none" : "border-jiojbmpurple/40 hover:bg-jiojbm20"}`}>
                <span className="font-semibold text-jiojbm50">{uploading ? "Uploading…" : "Click to upload LOA file(s)"}</span>
                <input type="file" multiple className="hidden" onChange={(e) => { uploadMedia(e.target.files); e.target.value = ""; }} />
              </label>
            )}

            {/* File list */}
            <div className="mt-4 max-h-64 overflow-auto">
              {mediaLoading ? (
                <p className="text-xs text-gray-500">Loading files…</p>
              ) : mediaFiles.length === 0 ? (
                <p className="text-xs text-gray-500">No files uploaded yet.</p>
              ) : (
                <ul className="divide-y">
                  {mediaFiles.map((f) => (
                    <li key={f} className="flex items-center justify-between gap-3 py-2">
                      <span className="text-sm text-gray-700 truncate">📄 {f}</span>
                      <span className="flex items-center gap-3 shrink-0">
                        <button className="text-xs font-semibold text-jiojbmpurple hover:underline" onClick={() => downloadMedia(f)}>Download</button>
                        {canManageFiles && <button className="text-xs font-semibold text-jiored1 hover:underline" onClick={() => deleteMedia(f)}>Remove</button>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!canManageFiles && <p className="mt-3 text-[11px] text-gray-400">Only partner accounts and L0 internal users can upload LOA files.</p>}
          </div>
        </div>
      )}
    </div>
  );
};
